import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../components/header";
import StoryMenu from "../../components/story/menu";
import StoryItem from "../../components/story/item";
import contentHooks from "../../hooks/contentHooks";
import { useNavigate } from "react-router-dom";

const StoryQuestion = () => {
  const navigate = useNavigate();
  const [questionData, setQuestionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastTimestamp, setLastTimestamp] = useState(0);

  const observerRef = useRef();
  const lastItemRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleItemClick = (id) => {
    navigate(`/story/question/${id}`);
  };

  const loadQuestions = useCallback(async (timestamp) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const result = await contentHooks.contentList({ 
        last_time_stamp: timestamp, 
        type: "궁금해요" 
      });

      if (result.IsError) {
        setError("데이터를 불러오는데 실패했습니다.");
        return;
      }

      const newQuestions = result.data.hits;
      
      if (newQuestions.length === 0) {
        setHasMore(false);
      } else {
        setQuestionData(prev => 
          timestamp === 0 ? newQuestions : [...prev, ...newQuestions]
        );
        setLastTimestamp(newQuestions[newQuestions.length - 1].created_at);
      }
    } catch (err) {
      console.error("데이터 로드 에러:", err);
      setError("네트워크 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  useEffect(() => {
    loadQuestions(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentLastItemRef = lastItemRef.current;

    const handleObserver = (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading && lastTimestamp > 0) {
        loadQuestions(lastTimestamp);
      }
    };

    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 0.5,
    };

    observerRef.current = new IntersectionObserver(handleObserver, option);

    if (currentLastItemRef && hasMore && !isLoading) {
      observerRef.current.observe(currentLastItemRef);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isLoading, hasMore, lastTimestamp, loadQuestions]);

  const handleRefresh = () => {
    setQuestionData([]);
    setLastTimestamp(0);
    setHasMore(true);
    setError(null);
    loadQuestions(0);
  };

  if (error) {
    return (
      <div
        className="flex flex-col pt-[3.125rem] md:pt-[4.375rem] bg-white"
        style={{ 
          minHeight: "calc(var(--vh, 1vh) * 100)",
          paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
        }}
      >
        <Header selectedMenu={1} />
        <div className="pb-[1.875rem]">
          <StoryMenu subMenuType={0} />
          <div className="text-center py-[1rem] text-red-500">
            <p>{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-[0.5rem] px-[1rem] py-[0.5rem] bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col pt-[3.125rem] md:pt-[4.375rem] bg-white"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <Header selectedMenu={1} />
      <div>
        <StoryMenu subMenuType={0} />
        <div className="text-center w-full">
          {questionData.length > 0 ? (
            questionData.map((item, index) => {
              const isLastItem = index === questionData.length - 1;
              
              return (
                <div
                  key={`${item.id}-${index}`}
                  ref={isLastItem ? lastItemRef : null}
                >
                  <StoryItem 
                    image={item.content_thumbnail} 
                    title={item.content_title} 
                    no={item.id}
                    onClick={() => handleItemClick(item.id)}
                  />
                </div>
              );
            })
          ) : !isLoading && !error ? (
            <div className="flex flex-col items-center justify-center py-[4rem]">
              <p className="text-gray-500 text-lg mb-[0.5rem]">아직 등록된 게시글이 없습니다.</p>
              <p className="text-gray-400 text-sm">곧 새로운 내용이 업데이트될 예정입니다.</p>
            </div>
          ) : null}
        </div>

        {isLoading && (
          <div className="flex justify-center py-[2rem]">
            <div className="animate-spin rounded-full h-[2rem] w-[2rem] border-b-2 border-gray-900"></div>
          </div>
        )}

        {!hasMore && questionData.length > 0 && (
          <div className="text-center py-[2rem] text-gray-500">
            모든 게시글을 불러왔습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryQuestion;
